import React, { ReactElement } from "react";
import {
  makeStyles,
  Theme,
  CircularProgress,
  Typography,
  Paper,
  Tooltip,
} from "@material-ui/core";
import { Translator } from "../services";
import clsx from "clsx";
import { Feature } from "../contexts/FeatureFlags";
import { ViCoreClaimTotalLoss } from "../models";

const useStyles = makeStyles((theme: Theme) => {
  return {
    value: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.2,
      "& span": {
        fontWeight: 200,
        color: theme.palette.grey.A200,
      },
    },

    label: {
      fontWeight: 400,
      fontSize: ".8rem",
      paddingBottom: "0.618rem",
      "&:last-child": { paddingBottom: 0 },
      lineHeight: 1.2,
    },

    totalLoss: {
      width: "100%",
      padding: "0 0.382rem",
      minWidth: "8rem",
      boxSizing: "border-box",
      marginBottom: "0.618rem",
      "&:last-child": { marginBottom: 0 },
    },

    labelBottom: {
      fontWeight: 400,
      fontSize: ".9rem",
    },

    loading: {
      margin: "0.382rem",
      "& svg": {
        width: "100%",
        height: "100%",
        color: theme.palette.secondary.light,
      },
    },
    metric: {
      padding: "0 0.382rem",
      minWidth: "50%",
      boxSizing: "border-box",
      marginBottom: "0.618rem",
      "&:last-child": { marginBottom: 0 },
    },
    wrapper: {
      position: "relative",
      gridArea: "stats",
      display: "flex",
      flexFlow: "row wrap",
      justifyContent: "flex-start",
      alignContent: "space-around",
      padding: "0.382rem",
      "&.loading": {
        placeContent: "center",
      },
    },
    tooltip: {
      cursor: "pointer",
    },
  };
});

function StatsCard({
  confidence,
  loading,
  imageCount,
  timing,
  numberOfDamagedParts,
  totalLoss,
}: {
  confidence: number | undefined;
  imageCount: number | undefined;
  timing?: number | undefined;
  numberOfDamagedParts: number | undefined;
  loading: boolean;
  totalLoss?: ViCoreClaimTotalLoss;
}): ReactElement | null {
  const classes = useStyles({ loading });

  return loading ||
    imageCount ||
    confidence ||
    timing ||
    numberOfDamagedParts ||
    totalLoss ? (
    <Paper elevation={3} className={clsx(classes.wrapper, loading && "loading")}>
      {loading ? (
        <CircularProgress size="5rem" disableShrink={true} className={classes.loading} />
      ) : (
        <>
          <div className={classes.metric}>
            <Typography className={classes.value}>
              {imageCount} {Translator.translate("components.statscard.images")}
            </Typography>
            <Typography className={classes.label}>
              {Translator.translate("components.statscard.imagesLabel")}
            </Typography>
          </div>
          {timing && (
            <div className={classes.metric}>
              <Typography className={classes.value}>
                {timing}
                {Translator.translate("components.statscard.timing")}
              </Typography>
              <Typography className={classes.label}>
                {Translator.translate("components.statscard.timingLabel")}
              </Typography>
            </div>
          )}
          {numberOfDamagedParts && numberOfDamagedParts > 0 ? (
            <>
              <div className={classes.metric}>
                <Typography className={classes.value}>
                  {numberOfDamagedParts}{" "}
                  {Translator.translate("components.statscard.parts")}
                </Typography>
                <Typography className={classes.label}>
                  {Translator.translate("components.statscard.partsLabel")}
                </Typography>
              </div>
              <div className={classes.metric}>
                <Typography className={classes.value}>
                  {confidence
                    ? Math.max(1, Math.round(confidence * 100)) + "%"
                    : Translator.translate("components.statscard.notApplicable")}
                </Typography>
                <Typography className={classes.label}>
                  {Translator.translate("components.statscard.confidenceLabel")}
                </Typography>
              </div>
            </>
          ) : null}
          {totalLoss ? (
            <Feature name="TotalLoss">
              <div className={classes.metric}>
                <Typography className={classes.value}>
                  {typeof totalLoss.totalLoss === "string" &&
                  totalLoss.totalLoss.length > 0
                    ? Translator.translate(
                        `components.statscard.totalLossLabel.${totalLoss.totalLoss.toUpperCase()}`
                      )
                    : Translator.translate(`components.statscard.notApplicable`)}
                  {totalLoss.p !== undefined && (
                    <Tooltip
                      title="Probability of Total Loss"
                      className={classes.tooltip}
                    >
                      <span> {Math.round(totalLoss.p * 100)}%</span>
                    </Tooltip>
                  )}
                </Typography>
                <Typography className={classes.label}>
                  {Translator.translate("components.statscard.totalLoss")}
                </Typography>
              </div>
            </Feature>
          ) : null}
        </>
      )}
    </Paper>
  ) : null;
}

export default React.memo(StatsCard);
